// Bilingual letter preview — modal.
//
// What's in the modal, top to bottom:
//   1. Title + subtitle (purpose + read-EN-then-send-PT framing)
//   2. Escalation ladder (●─○─○) — first letter / registered notice / court
//      so the user sees this is step 1 of a defined path, not a one-shot.
//   3. Investment strip — "Drafted from {events} timeline events you
//      confirmed and {files} uploaded documents" — anti-AI-slop signal
//      grounded in the user's own work (IKEA Effect).
//   4. EN/PT segmented tabs (EN by default — the expat ICP reads English).
//   5. The full letter, mirroring the PDF: From/To header → subject →
//      greeting → narrative paragraphs → total paid → Pedido callout →
//      courtesy closing → sign-off → signature → disclaimer.
//   6. Footer: meta + Regenerate on the left, Cancel + Download on the right.
//
// Why render the full letter (not just narrative + Pedido): the previous
// shape stopped at Pedido and the user couldn't see what they were
// actually sending. The PDF has a complete structure — modal must mirror
// it so the EN-tab review is faithful and there are no dangling self-
// references like "no prazo indicado abaixo".

import { useEffect, useState } from 'react';
import { useGenerateFirmMessage } from '../artifacts';
import { useFiles } from '../files';
import {
  type CaseNarrative,
  type LetterParties,
  useNarrative,
  useRefreshNarrative,
} from '../narrative';
import { m } from '../paraglide/messages';
import { useTimeline } from '../timeline';

type Pane = 'en' | 'pt';

export function LetterPreviewModal({
  caseId,
  open,
  onClose,
}: {
  caseId: string;
  open: boolean;
  onClose: () => void;
}) {
  const narrativeQ = useNarrative(open ? caseId : undefined);
  const refresh = useRefreshNarrative(caseId);
  const generate = useGenerateFirmMessage(caseId);
  const timeline = useTimeline(open ? caseId : undefined);
  const files = useFiles(open ? caseId : undefined);
  const [pane, setPane] = useState<Pane>('en');

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setPane('en');
  }, [open]);

  if (!open) return null;

  const eventCount = timeline.data?.length ?? 0;
  const fileCount = files.data?.length ?? 0;
  const narrative = narrativeQ.data?.narrative ?? null;
  const parties = narrativeQ.data?.parties ?? null;
  const amountPaid = narrativeQ.data?.amount_paid_eur ?? null;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc handling is wired via window listener
    <dialog
      className="modal-backdrop"
      open
      aria-label="Letter preview"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-shell letter-modal">
        <header className="letter-modal-head">
          <div className="letter-modal-title-block">
            <h2 className="letter-modal-title">{m.letterPreviewTitle()}</h2>
            <p className="letter-modal-subtitle">{m.letterPreviewIntro()}</p>
          </div>
          <button type="button" className="btn-ghost btn-xs" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <EscalationLadder />

        <InvestmentStrip events={eventCount} files={fileCount} />

        <Body
          narrative={narrative}
          parties={parties}
          amountPaid={amountPaid}
          isLoading={narrativeQ.isLoading}
          isError={narrativeQ.isError}
          pane={pane}
          onPane={setPane}
        />

        <footer className="letter-modal-foot">
          <div className="letter-modal-meta">
            {narrative ? (
              <>
                <span className="muted small">
                  {m.letterPreviewGeneratedAt({
                    when: formatRelative(narrative.generated_at),
                  })}
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => refresh.mutate()}
                  disabled={refresh.isPending}
                >
                  {refresh.isPending ? m.letterPreviewRefreshing() : m.letterPreviewRefresh()}
                </button>
              </>
            ) : null}
          </div>
          <div className="letter-modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {m.letterPreviewCancel()}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => generate.mutate(undefined, { onSuccess: () => onClose() })}
              disabled={generate.isPending || !narrative}
            >
              {generate.isPending ? m.letterPreviewDownloading() : m.letterPreviewDownload()}
            </button>
          </div>
        </footer>
      </div>
    </dialog>
  );
}

// ── Escalation ladder ───────────────────────────────────────────────────────

function EscalationLadder() {
  return (
    <section className="letter-modal-ladder" aria-label={m.letterPreviewLadderTitle()}>
      <ol>
        <li className="is-current">
          <span className="ladder-dot" aria-hidden="true" />
          <span className="ladder-label">{m.letterPreviewLadderStep1()}</span>
          <span className="ladder-detail muted small">{m.letterPreviewLadderStep1Detail()}</span>
        </li>
        <li>
          <span className="ladder-dot" aria-hidden="true" />
          <span className="ladder-label">{m.letterPreviewLadderStep2()}</span>
          <span className="ladder-detail muted small">{m.letterPreviewLadderStep2Detail()}</span>
        </li>
        <li>
          <span className="ladder-dot" aria-hidden="true" />
          <span className="ladder-label">{m.letterPreviewLadderStep3()}</span>
          <span className="ladder-detail muted small">{m.letterPreviewLadderStep3Detail()}</span>
        </li>
      </ol>
    </section>
  );
}

// ── Investment strip ────────────────────────────────────────────────────────

function InvestmentStrip({ events, files }: { events: number; files: number }) {
  let text: string;
  if (events === 1 && files === 1) {
    text = m.letterPreviewSourcedFromOneEvent({ files: '1' });
  } else if (events === 1) {
    text = m.letterPreviewSourcedFromOneEvent({ files: String(files) });
  } else if (files === 1) {
    text = m.letterPreviewSourcedFromOneFile({ events: String(events) });
  } else {
    text = m.letterPreviewSourcedFrom({ events: String(events), files: String(files) });
  }
  return <p className="letter-modal-investment small muted">{text}</p>;
}

// ── Body — full letter mirror ──────────────────────────────────────────────

function Body({
  narrative,
  parties,
  amountPaid,
  isLoading,
  isError,
  pane,
  onPane,
}: {
  narrative: CaseNarrative | null;
  parties: LetterParties | null;
  amountPaid: number | null;
  isLoading: boolean;
  isError: boolean;
  pane: Pane;
  onPane: (p: Pane) => void;
}) {
  if (isLoading) {
    return (
      <div className="letter-modal-body letter-modal-body--state">
        <p className="muted">{m.letterPreviewLoading()}</p>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="letter-modal-body letter-modal-body--state">
        <p className="notice error">{m.letterPreviewError()}</p>
      </div>
    );
  }
  if (!narrative || narrative.pt_paragraphs.length === 0 || !parties) {
    return (
      <div className="letter-modal-body letter-modal-body--state">
        <p className="muted">{m.letterPreviewEmpty()}</p>
      </div>
    );
  }

  const isPT = pane === 'pt';
  const paragraphs = isPT ? narrative.pt_paragraphs : narrative.en_paragraphs;
  const pedido = isPT ? narrative.pedido_pt : narrative.pedido_en;
  const subject = isPT ? m.letterPreviewSubjectPT() : m.letterPreviewSubjectEN();
  const greeting = isPT ? m.letterPreviewGreetingPT() : m.letterPreviewGreetingEN();
  const totalLabel = isPT ? m.letterPreviewTotalPaidLabelPT() : m.letterPreviewTotalPaidLabelEN();
  const closing = isPT ? m.letterPreviewClosingPT() : m.letterPreviewClosingEN();
  const signoff = isPT ? m.letterPreviewSignoffPT() : m.letterPreviewSignoffEN();
  const disclaimer = isPT ? m.letterPreviewDisclaimerPT() : m.letterPreviewDisclaimerEN();

  return (
    <>
      <div className="letter-modal-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={pane === 'en'}
          className={`letter-modal-tab${pane === 'en' ? ' is-active' : ''}`}
          onClick={() => onPane('en')}
        >
          {m.letterPreviewTabEN()}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pane === 'pt'}
          className={`letter-modal-tab${pane === 'pt' ? ' is-active' : ''}`}
          onClick={() => onPane('pt')}
        >
          {m.letterPreviewTabPT()}
        </button>
      </div>
      <article
        className="letter-modal-body letter-modal-letter"
        lang={pane === 'pt' ? 'pt-PT' : 'en'}
        aria-live="polite"
      >
        <FromToHeader parties={parties} />

        <p className="letter-modal-subject">
          <strong>{subject}</strong>
        </p>

        <p className="letter-modal-greeting">{greeting}</p>

        {paragraphs.map((p, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: paragraphs are immutable per render and have no stable id
          <p key={i}>{p}</p>
        ))}

        {amountPaid != null ? (
          <p className="letter-modal-total">
            <strong>{totalLabel}</strong> {formatEur(amountPaid, pane)}
          </p>
        ) : null}

        {pedido ? (
          <aside className="letter-modal-pedido">
            <span className="letter-modal-pedido-label">{m.letterPreviewPedidoLabel()}</span>
            <p>{pedido}</p>
          </aside>
        ) : null}

        <p className="letter-modal-closing">{closing}</p>

        <p className="letter-modal-signoff">{signoff}</p>
        <p className="letter-modal-signature">{parties.owner}</p>

        <p className="letter-modal-anchor small muted">{m.letterPreviewLawyerAnchor()}</p>

        <p className="letter-modal-disclaimer small muted">{disclaimer}</p>
      </article>
    </>
  );
}

function FromToHeader({ parties }: { parties: LetterParties }) {
  return (
    <div className="letter-modal-fromto">
      <div>
        <span className="letter-modal-fromto-label">{m.letterPreviewFromLabel()}</span>
        <span className="letter-modal-fromto-value">{parties.owner}</span>
        {parties.location && parties.location !== '[morada da obra]' ? (
          <span className="letter-modal-fromto-sub muted">{parties.location}</span>
        ) : null}
      </div>
      <div className="letter-modal-fromto-right">
        <span className="letter-modal-fromto-label">{m.letterPreviewToLabel()}</span>
        <span className="letter-modal-fromto-value">{parties.contractor}</span>
      </div>
    </div>
  );
}

/** PT: "1.800,00 €". EN: "€1,800.00". Currency formatting per locale. */
function formatEur(value: number, pane: Pane): string {
  const locale = pane === 'pt' ? 'pt-PT' : 'en-GB';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value} €`;
  }
}

/** Best-effort relative date for the "Drafted X" line. */
function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}
